import { useRef } from "react";
import Zoom from "react-medium-image-zoom";
import Slider, { Settings } from "react-slick";
import { TestIdClassNameProps } from "@app/types/Common";
import { Media } from "@app/types/Cue";
import { CaaSImageFilters } from "@app/types/OptimisedImage";
import ArticleThumbnail from "@components/ArticleThumbnail/ArticleThumbnail";
import { useOnLoadImages } from "@hooks/useOnLoadImages";
import {
  getDimensionsForFilter,
  getImageCaptionCreditFromSummary,
} from "@util/helpers";

import ArticleMainImageArrow from "./ArticleMainImageArrow";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

type ArticleMainImageProps = TestIdClassNameProps & {
  media: Media[];
  captionRootClassname?: string;
  imgRootClassName?: string;
  noCaption?: boolean;
  thumbnailRootClassName?: string;
  landscapeThumbnailRootClassName?: string;
  landscapeImgRootClassName?: string;
  invisibleImgRootClass?: string;
};
export default function ArticleMainImage({
  rootClassName,
  media,
  captionRootClassname,
  imgRootClassName,
  thumbnailRootClassName = "bg-gray-250",
  noCaption = false,
  landscapeThumbnailRootClassName = "",
  landscapeImgRootClassName = "",
  invisibleImgRootClass = "",
}: ArticleMainImageProps): React.ReactElement {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imagesLoaded = useOnLoadImages(wrapperRef);

  const SliderComponent =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    typeof window === "undefined" ? Slider.default : Slider;

  const settings: Settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToScroll: 1,
    slidesToShow: 1,
    prevArrow: <ArticleMainImageArrow direction="left" />,
    nextArrow: <ArticleMainImageArrow direction="right" />,
    swipeToSlide: true,
  };

  return (
    <>
      {media.length > 0 ? (
        <div
          className={rootClassName}
          data-testid="article-main-image-component"
        >
          <SliderComponent {...settings}>
            {media.map(({ content, ...rest }) => {
              const thumbnail = content
                ? { ...content, alt: "" }
                : { ...rest, alt: "" };

              const metadata = content?.fields?.com_escenic_defaultmetadata;

              const filters: CaaSImageFilters[] = [
                {
                  breakpoint: "(min-width: 992px)",
                  ...getDimensionsForFilter(metadata, { w: 854, h: 1281 }),
                  f: "webp",
                },
                {
                  ...getDimensionsForFilter(metadata, { w: 900, h: 600 }),
                  f: "webp",
                },
              ];

              return (
                <div className="relative" key={content?.id} ref={wrapperRef}>
                  {!imagesLoaded ? (
                    <div className="aspect-3x2 w-full bg-gray-250"></div>
                  ) : (
                    <>
                      <Zoom zoomMargin={32} classDialog="medium-zoom">
                        <ArticleThumbnail
                          thumbnail={thumbnail}
                          filters={filters}
                          rootClassName={thumbnailRootClassName}
                          imgRootClassName={imgRootClassName}
                          landscapeThumbnailRootClassName={
                            landscapeThumbnailRootClassName
                          }
                          landscapeImgRootClassName={landscapeImgRootClassName}
                          invisibleImgRootClass={invisibleImgRootClass}
                        />
                      </Zoom>

                      {!noCaption ? (
                        <div className={captionRootClassname}>
                          {getImageCaptionCreditFromSummary(rest.summary)}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              );
            })}
          </SliderComponent>
        </div>
      ) : null}
    </>
  );
}
